import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {ReactComponent as FlipToLandscape} from 'assets/images/icons/icon-flip-to-landscape.svg';
import {scrollTo} from 'helpers/scroll-helper';
import {shuffleArray} from 'helpers/array-helper';
import {getText} from 'helpers/language-helper';
import TaskIntro from '../task-intro/task-intro';
import Carousel from './carousel';
import Audio from 'components/ui/audio/audio';
import './dialogue.scss';

const Dialogue = (props) => {
	const {
		languageId,
		playerTaskData, 
		taskData,
		moduleData,
		deviceInfo,
		updateLoggedTime,
		handleInstantTaskEffects, 
		handleCompleteTask
	} = props;

	/* Timeout */
	let timeout = null;	
	let timeout2 = null;

	/* Task id */
	const [taskId, setTaskId] = useState(null);

	/* Pause */
	const [isPaused, setIsPaused] = useState(false);

	/* Check if completed already */
	const isCompleted = (playerTaskData && playerTaskData.isCompleted === true ? true : false);

	/* Track dialogue progress & points */
	const [dialogueArr, setDialogueArr] = useState([]);
	const [points, setPoints] = useState(0);

	/* Current step options */
	const [currentStepOptions, setCurrentStepOptions] = useState([]);

	/* Current dialogue step data */
	const currentDialogueStepData = (isCompleted || dialogueArr.length === 0
		? null
		: taskData.steps.find((step) => {return step.id === dialogueArr[0].stepId;})
	);

	/**
	 * Get points
	 * @returns {number} points
	 */
	const getPoints = () => {
		let points = 0;
		if (isCompleted) points = playerTaskData.points;
		return points;
	};

	/**
	 * Prepare dialogue
	 * @returns {array} newDialogueArr
	 */
	const prepareDialogue = () => {
		let newDialogueArr = [];

		if (
			playerTaskData && 
			playerTaskData.isCompleted && 
			playerTaskData.dialogueArr && 
			playerTaskData.dialogueArr.length > 0
		) {
			newDialogueArr = [...playerTaskData.dialogueArr];
		} else {
			const firstDialogueStepData = (taskData && taskData.steps && taskData.steps.length > 0 
				? taskData.steps[0] 
				: null
			);
			if (firstDialogueStepData) {
				newDialogueArr.push({stepId: firstDialogueStepData.id});
			}			
		}
		return newDialogueArr;
	};

	/**
	 * Smooth scroll to dialogue bottom
	 */
	const scrollToBottom = () => {
		var div = document.getElementById('dialogue');
		if (div) {
			scrollTo(div, (div.scrollHeight - div.clientHeight), 0.1, null);
		}
	};

	/**
	 * Select an option (answer or action)
	 * @param {number} optionId 
	 */
	const selectOption = (optionId) => {
		/* Cannot play if game is paused */
		if (isPaused || isCompleted) return;

		/* Pause game */
		setIsPaused(true);

		/* Update logged time */
		updateLoggedTime();

		/* Get selected option data */
		const selectedOptionData = (currentDialogueStepData && currentDialogueStepData.options
			? currentDialogueStepData.options.find((option) => {return option.id === optionId;})
			: null
		);
		if (selectedOptionData) {
			/* Prepare instant effects */
			const instantEffects = [];

			/* Update points */
			let newPoints = 0;
			if (selectedOptionData.effects && selectedOptionData.effects.length > 0) {
				selectedOptionData.effects.forEach((effect) => {
					if (effect.type === 'points') {
						/* Effect: points */
						newPoints += effect.value;	
					} else {
						/* Effect: feedback, popup, special points, avatar item */
					}
				});
			}
			const totalPoints = points + newPoints;
			setPoints(totalPoints);

			/* Update dialogue array */
			const newDialogueArr = JSON.parse(JSON.stringify(dialogueArr));
			if (newDialogueArr.length > 0) {
				newDialogueArr[newDialogueArr.length - 1].optionId = optionId;
				newDialogueArr[newDialogueArr.length - 1].points = newPoints;
				newDialogueArr[newDialogueArr.length - 1].isCompleted = true;
			}
			setDialogueArr(newDialogueArr);

			/* Instant effects */
			if (instantEffects.length > 0) handleInstantTaskEffects(instantEffects);

			if (selectedOptionData.nextStepId) {
				/* Go to next step */
				if (timeout) clearTimeout(timeout);
				timeout = setTimeout(() => {
					goToNextDialogueStep(selectedOptionData.nextStepId, newDialogueArr, totalPoints);
				}, 1000);
			} else {
				/* No next step, complete task */
				completeTask(newDialogueArr, totalPoints);
			}
		}
	};

	/**
	 * Go to next dialogue step
	 * @param {string} nextStepId
	 * @param {array} newDialogueArr 
	 * @param {number} totalPoints
	 */
	const goToNextDialogueStep = (nextStepId, newDialogueArr, totalPoints) => {
		/* Hide options */
		setIsPaused(true);
		setCurrentStepOptions([]);

		/* Prepare new dialogue array */
		const updatedDialogueArr = JSON.parse(JSON.stringify(newDialogueArr));

		/* Get next step data */
		const nextDialogueStepData = taskData.steps.find((step) => {return step.id === nextStepId;});
		if (nextDialogueStepData) {
			updatedDialogueArr.push({stepId: nextStepId});
			setDialogueArr(updatedDialogueArr);
			if (nextDialogueStepData.options && nextDialogueStepData.options.length > 0) {
				/* Shuffle options */ 
				const shuffledOptions = shuffleArray(nextDialogueStepData.options);

				/* Show next step */
				setIsPaused(false);
				setCurrentStepOptions(shuffledOptions);
			} else if (nextDialogueStepData.nextStepId) {
				/* Continue to next next step */
				if (timeout2) clearTimeout(timeout2);
				timeout2 = setTimeout(() => {
					goToNextDialogueStep(nextDialogueStepData.nextStepId, updatedDialogueArr, totalPoints);
				}, 1000);
			} else {
				/* Complete task */
				setIsPaused(false);
				completeTask(updatedDialogueArr, totalPoints);
			}
		} else {
			setIsPaused(false);
		}
	};


	/**
	 * Complete task
	 * @param {array} finalDialogueArray
	 * @param {number} newPoints
	 */
	const completeTask = (finalDialogueArray, newPoints) => {
		/* Errors */
		const errors = 0;

		/* Save completed task */
		handleCompleteTask(
			'dialogue', 
			newPoints, 
			errors, 
			[],
			{dialogueArr: finalDialogueArray}
		);
	};

	/**
	 * Auto-scroll to bottom when story array is updated
	 */
	useEffect(() => {
		if (dialogueArr.length > 1) {
			scrollToBottom();
		}
		
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dialogueArr]);


	/**
	 * New dialogue task, reset
	 */
	useEffect(() => {
		/* Get initial dialogue array */
		const initialDialogueArray = prepareDialogue();

		if (!isCompleted && initialDialogueArray.length > 0) {
			/* Shuffle options of first step (if there are any) */
			const initialDialogueStepData = taskData.steps.find((step) => {
				return step.id === initialDialogueArray[0].stepId;
			});
			if (
				initialDialogueStepData && 
				initialDialogueStepData.options && 
				initialDialogueStepData.options.length > 0
			) {
				const shuffledOptions = shuffleArray(initialDialogueStepData.options);
				setCurrentStepOptions(shuffledOptions);
			}
		}
		setDialogueArr(initialDialogueArray);
		setTaskId(taskData.id);
		setPoints(getPoints());
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [taskData.id]);

	/**
	 * Component mounted / will unmount
	 */
	useEffect(() => {
		/* Component mounted */
		// ...

		return () => {
			/* Component will unmount */
			if (timeout) clearTimeout(timeout);
			if (timeout2) clearTimeout(timeout2);
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);


	return (
		<div className={'Dialogue ' + deviceInfo.orientation}>
			{deviceInfo.orientation === 'portrait' &&
				<FlipToLandscape className='Dialogue-flipToLandscape'/>
			}
			{taskData.id === taskId && <div className="Dialogue-content">
				<div id="taskIntro" className="Dialogue-intro">
					<TaskIntro 
						languageId={languageId}
						moduleId={moduleData.id}
						text={taskData.text ? getText(taskData.text, languageId) : null}
						image={taskData.image}
						fileName={languageId + '-' + taskData.taskId}
					/>
				</div>

				<div id="dialogue" className={'Dialogue-dialogue ' + deviceInfo.orientation}>
					{dialogueArr.map((dialogueStep, index) => {
						const dialogueStepData = 
							taskData.steps.find((step) => {return step.id === dialogueStep.stepId;});

						if (!dialogueStepData) return null;
						
						let selectedOptionData = null;
						if (dialogueStep.optionId) {
							selectedOptionData = (dialogueStepData && dialogueStepData.options
								? dialogueStepData.options.find((option) => {
									return option.id === dialogueStep.optionId;
								})
								: null
							);
						}
						return (
							<div key={index} className={'Dialogue-step'}>
								{/* Character / action */}
								{dialogueStepData.text && 
									<div className={'Dialogue-line ' 
										+ (dialogueStepData.type === 'dialogue' ? 'character' : 'action')}>
										{dialogueStepData.type === 'dialogue' && <div className={'Dialogue-character ' 
											+ taskData.characterId + ' ' + dialogueStepData.characterMood} />}
										<div className={'Dialogue-bubble ' + deviceInfo.orientation}>
											<span>{getText(dialogueStepData.text, languageId)}</span>
											<div className="Dialogue-bubbleAudio">
												<Audio 
													type='task-intro'
													color='blue'
													fileName={languageId + '-' + taskData.taskId 
														+ '-' + dialogueStepData.id}
												/>
											</div>
										</div>
									</div>
								}
								{/* Player */}
								{(selectedOptionData && selectedOptionData.text) && 
									<div className={'Dialogue-line player'}>
										<div className={'Dialogue-bubble ' + deviceInfo.orientation}>
											<span>{getText(selectedOptionData.text, languageId)}</span>
											<div className="Dialogue-bubbleAudio">
												<Audio 
													type='task-intro'
													color='blue'
													fileName={languageId + '-' + taskData.taskId 
														+ '-' + dialogueStepData.id + '-' + selectedOptionData.id}
												/>
											</div>
										</div>
									</div>
								}
							</div>
						);
					})}
				</div>

				{/* Bottom panel */}
				{!isCompleted && <div className={'Dialogue-panel ' + deviceInfo.orientation}>
					{/* Options */}
					{currentStepOptions.length > 0 && 
						<div className="Dialogue-options">
							<div className="Dialogue-optionsText">
								<span>{getText(currentDialogueStepData.optionPrompt, languageId)}</span>
							</div>
							<div className="Dialogue-optionsCarousel">
								<Carousel 
									taskId={taskData.taskId}
									currentStepId={currentDialogueStepData.id}
									languageId={languageId}
									optionType={currentDialogueStepData.optionType}
									options={currentStepOptions} 
									selectOption={selectOption}
									deviceInfo={deviceInfo}
								/>
							</div>
						</div>
					}
				</div>}
			</div>}
		</div>
	);
};

Dialogue.propTypes = {
	languageId: PropTypes.string.isRequired,
	playerTaskData: PropTypes.object,
	taskData: PropTypes.object.isRequired,
	moduleData: PropTypes.object.isRequired,
	deviceInfo: PropTypes.object.isRequired,
	updateLoggedTime: PropTypes.func.isRequired,
	handleInstantTaskEffects: PropTypes.func.isRequired,
	handleCompleteTask: PropTypes.func.isRequired
};

export default Dialogue;
